import { ReactNode, useEffect, useRef } from "react";
import { useAuth } from "./AuthProvider";
import { useNavigate, useLocation } from "react-router-dom";
import { rememberAdminReturnPath } from "@/lib/adminRoutePersistence";

interface Props { children: ReactNode }

const RequireAuth = ({ children }: Props) => {
  const { isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Track whether we've EVER been an admin in this session.
  // Once we have, brief loading states (token refresh, tab focus) should
  // not unmount the children — otherwise all in-page state is lost.
  const wasAdminRef = useRef(false);
  if (isAdmin) wasAdminRef.current = true;

  useEffect(() => {
    if (!loading && !isAdmin && !wasAdminRef.current) {
      rememberAdminReturnPath(`${location.pathname}${location.search}`);
      navigate('/admin', { replace: true, state: { from: location } });
    }
  }, [loading, isAdmin, navigate, location]);

  // Show loading only on the very first check, before we've been admin.
  if (loading && !wasAdminRef.current) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }
  if (!isAdmin && !wasAdminRef.current) return null;
  return <>{children}</>;
};

export default RequireAuth;
